import { Cloud, CloudOff, RefreshCw, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { syncService, SyncStatus } from '@/services/sync-service';
import { useState, useEffect } from 'react';
import { offlineDB } from '@/lib/offline-db';
import { useAuth } from '@/contexts/AuthContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const OfflineBadge = () => {
  const { user } = useAuth();
  const networkStatus = useNetworkStatus();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ 
    status: 'idle', 
    progress: 0 
  });
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const loadPendingCount = async () => {
      if (!user) return;
      const runs = await offlineDB.getPendingRuns(user.id);
      setPendingCount(runs.length);
    };

    loadPendingCount();
    const interval = setInterval(loadPendingCount, 5000);

    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const unsubscribe = syncService.onSyncStatusChange(setSyncStatus);
    return unsubscribe;
  }, []);

  const handleManualSync = async () => {
    if (!user) return;
    try {
      await syncService.syncPendingRuns(user.id);
    } catch (error) {
      console.error('Manual sync failed', error);
    }
  };

  if (!networkStatus.isOnline && pendingCount === 0) {
    return (
      <Badge variant="secondary" className="gap-2">
        <CloudOff className="w-3 h-3" />
        Offline
      </Badge>
    );
  }

  if (pendingCount === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Badge 
          variant={syncStatus.status === 'syncing' ? 'default' : 'destructive'}
          className="gap-2 cursor-pointer"
        >
          {syncStatus.status === 'syncing' ? (
            <>
              <RefreshCw className="w-3 h-3 animate-spin" />
              Sincronizando... {syncStatus.progress.toFixed(0)}%
            </>
          ) : (
            <>
              <AlertCircle className="w-3 h-3" />
              {pendingCount} corrida{pendingCount > 1 ? 's' : ''} pendente{pendingCount > 1 ? 's' : ''}
            </>
          )}
        </Badge>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-1">Corridas Offline</h4>
            <p className="text-sm text-muted-foreground">
              Você tem {pendingCount} corrida{pendingCount > 1 ? 's' : ''} aguardando sincronização
            </p>
          </div>

          {networkStatus.isOnline ? (
            <Button 
              onClick={handleManualSync}
              disabled={syncStatus.status === 'syncing'}
              className="w-full"
              size="sm"
            >
              {syncStatus.status === 'syncing' ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sincronizando...
                </>
              ) : (
                <>
                  <Cloud className="w-4 h-4 mr-2" />
                  Sincronizar Agora
                </>
              )}
            </Button>
          ) : (
            <div className="text-sm text-muted-foreground flex items-center gap-2 p-2 bg-muted rounded">
              <CloudOff className="w-4 h-4" />
              Aguardando conexão...
            </div>
          )}

          {syncStatus.status === 'error' && (
            <div className="text-sm text-destructive p-2 bg-destructive/10 rounded">
              Erro na sincronização. Tentando novamente...
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
